import {Button, createStyles, Group} from '@mantine/core';
import React from 'react';

import {ACTION_CATEGORY} from 'analytics/AnalyticsTypes';
import * as NavConstants from 'constants/NavConstants';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChatWithDraftMessage} from 'llm/chat-library/ChatActions';
import {PROGRESSIVE_DISCLOSURE_CATEGORIES} from 'llm/chat-library/progressive-disclosure/ProgressiveDisclosureData';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import CardSection from './CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT} from './HomepageConstants';
import {trackHomepageEvent} from './HomepageUtils';

const useStyles = createStyles((theme) => ({
  common: {
    paddingRight: theme.spacing.xs,
  },
  'capture-data': {
    color: theme.colors.grape[6],
  },
  'analyze-data': {
    color: theme.colors.green[6],
  },
  'write-reports': {
    color: theme.colors.teal[6],
  },
}));

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.AI;

async function handleChatCreation(prompt: string): Promise<void> {
  trackHomepageEvent('ai_chat_button_clicked', ACTION_CATEGORY.WRITE);

  const result = await createChatWithDraftMessage(ChatType.UNIFIED, {
    createParams: {contextIds: []},
    draft: {
      status: ChatDraftStatus.DRAFT,
      message: {text: prompt, selectedFiles: []},
      settings: {agentType: AgentType.ASSISTANT, publicDataEnabled: false},
    },
  });

  openExistingChat(result.chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);
}

const PROMPT_BUTTONS: Array<{
  prompt: string;
  label: string;
  id: string;
  categoryId: string;
  icon: BenchlingIconName;
}> = PROGRESSIVE_DISCLOSURE_CATEGORIES.flatMap(({icon, id: categoryId, options}) =>
  options.map(({prompt, label, id}) => ({
    prompt,
    label,
    id,
    categoryId,
    icon,
  }))
);

export default function AISection(): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <CardSection
      title="Ask Benchling AI"
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
      noBorder={true}
    >
      <Group spacing="md">
        {PROMPT_BUTTONS.map(({prompt, label, id, categoryId, icon}) => (
          <Button
            key={id}
            onClick={() => handleChatCreation(prompt)}
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design, yet
            variant="default"
            size="md"
            radius="lg"
            fw={400}
            leftIcon={
              <BenchlingIcon
                icon={icon}
                className={cx(classes[categoryId as keyof typeof classes], classes.common)}
              />
            }
          >
            {label}
          </Button>
        ))}
      </Group>
    </CardSection>
  );
}
